import { Brain, Database, ShoppingCart, Zap, BarChart3, ArrowRight, CheckCircle, Rocket, Code } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { useNavigate } from "react-router-dom";
import { SEOHead } from "@/components/SEO";
import { ServiceSchema } from "@/components/Schema";

const Services = () => {
  const navigate = useNavigate();
  
  const services = [
    {
      icon: Brain,
      title: "AI Solutions & Machine Learning",
      description: "Revolutionary AI applications with machine learning models, neural networks, and intelligent automation that deliver measurable ROI.",
      features: [
        "GPT-4 & Claude Integration",
        "Computer Vision & OCR",
        "Predictive Analytics",
        "AI Workflow Automation"
      ],
      timeline: "4-8 weeks",
      caseStudy: "Increased client efficiency by 300% with custom AI workflow automation",
      color: "from-blue-500 to-cyan-400"
    },
    {
      icon: Database,
      title: "NetSuite Excellence",
      description: "Complete NetSuite ERP transformations with custom modules, advanced reporting, and intelligent automation for enterprise scalability.",
      features: [
        "SuiteCloud Development",
        "Advanced Workflows",
        "Custom SuiteScript",
        "Financial Automation"
      ],
      timeline: "6-12 weeks",
      caseStudy: "Reduced operational costs by 45% with automated NetSuite workflows",
      color: "from-indigo-500 to-purple-400"
    },
    {
      icon: ShoppingCart,
      title: "Shopify Plus Development",
      description: "Enterprise-grade e-commerce solutions with AI-powered personalization, advanced analytics, and seamless third-party integrations.",
      features: [
        "Headless Commerce",
        "AI Product Recommendations",
        "Custom App Development",
        "Performance Optimization"
      ],
      timeline: "3-6 weeks",
      caseStudy: "Generated $2M+ additional revenue through AI-powered product recommendations",
      color: "from-emerald-500 to-teal-400"
    },
    {
      icon: Zap,
      title: "Celigo Integration",
      description: "Sophisticated iPaaS solutions connecting 50+ applications with real-time data synchronization, error handling, and monitoring.",
      features: [
        "Real-time Data Sync",
        "Advanced Error Handling",
        "API Gateway Management",
        "Webhook Automation"
      ],
      timeline: "4-10 weeks",
      caseStudy: "Connected 25+ business systems with high availability",
      color: "from-orange-500 to-amber-400"
    },
    {
      icon: BarChart3,
      title: "PowerBI & Analytics",
      description: "Enterprise-grade business intelligence with AI-powered insights, real-time dashboards, and predictive modeling capabilities.",
      features: [
        "AI-Powered Insights",
        "Real-time Dashboards",
        "Predictive Modeling",
        "Data Visualizations"
      ],
      timeline: "2-4 weeks",
      caseStudy: "Enabled real-time decision making for a Fortune 500 logistics company",
      color: "from-yellow-500 to-orange-400"
    },
    {
      icon: Code,
      title: "Custom Software",
      description: "Bespoke software solutions built on modern architectures to solve unique business challenges that off-the-shelf software can't handle.",
      features: [
        "Full-Stack Development",
        "Cloud Architecture",
        "Microservices",
        "Legacy Modernization"
      ],
      timeline: "8-16 weeks",
      caseStudy: "Built a proprietary trading platform processing $50M+ daily volume",
      color: "from-pink-500 to-rose-400"
    }
  ];

  return (
    <>
      <SEOHead
        title="Enterprise Technology Services | AI, NetSuite, Shopify | FortifyTek"
        description="Transform your business with our technology services. Specializing in AI solutions, NetSuite ERP, Shopify Plus, and enterprise integration."
        keywords={[
          'AI Development Services',
          'NetSuite Implementation',
          'Shopify Plus Agency',
          'Enterprise Integration',
          'Custom Software Development',
          'Data Analytics Services'
        ]}
        canonicalUrl="https://www.fortifytek.com/services"
        schemaType="Service"
      />
      <ServiceSchema 
        service={{
          name: "Enterprise Technology Services",
          description: "Comprehensive technology services including AI solutions, NetSuite ERP implementation, Shopify Plus development, and enterprise integration.",
          url: "https://www.fortifytek.com/services",
          category: "Technology Services",
          serviceType: "IT Consulting"
        }}
      />
      
      <div className="min-h-screen bg-background flex flex-col">
        <Navigation />
        
        <main className="flex-grow">
          {/* Hero Section */}
          <section className="relative px-6 lg:px-8 mb-24 pt-40 pb-20">
            <div className="absolute inset-0 -z-10 overflow-hidden">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-primary/20 rounded-full blur-[120px] opacity-30"></div>
            </div>
            
            <div className="max-w-4xl mx-auto text-center animate-fade-up">
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white mb-8 leading-tight">
                <span className="block mb-2">
                  Services that
                </span>
                <span className="block text-amber-400 pb-2">
                  Transform Your Business
                </span>
              </h1>
              
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
                We combine cutting-edge AI, enterprise-grade engineering, and strategic innovation to build solutions that drive real growth.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  className="bg-white text-black hover:bg-zinc-200 px-8 h-12 text-lg"
                  onClick={() => navigate('/contact')}
                >
                  Start Your Project
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="border-zinc-700 hover:bg-zinc-800 text-white h-12 text-lg"
                  onClick={() => document.getElementById('services-grid')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Explore Services
                </Button>
              </div>
            </div>
          </section>

          {/* Services Grid */}
          <section id="services-grid" className="container mx-auto px-6 mb-32">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div 
                  key={index}
                  className="group relative glass-panel rounded-3xl p-8 hover:bg-white/10 transition-all duration-500 hover:-translate-y-2 overflow-hidden"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Gradient Glow Effect on Hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
                  
                  <div className="relative z-10">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${service.color} p-0.5 mb-6 shadow-lg`}>
                      <div className="w-full h-full bg-background rounded-2xl flex items-center justify-center">
                        <service.icon className="w-7 h-7 text-white" />
                      </div>
                    </div>
                    
                    <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-amber-400 transition-colors">
                      {service.title}
                    </h3>
                    
                    <p className="text-muted-foreground mb-8 leading-relaxed">
                      {service.description}
                    </p>
                    
                    <div className="space-y-4 mb-8">
                      <h4 className="text-sm font-semibold text-white/80 uppercase tracking-wider">Key Features</h4>
                      <ul className="space-y-3">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start text-sm text-muted-foreground">
                            <CheckCircle className="w-5 h-5 text-amber-400 mr-3 shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="pt-6 border-t border-white/10">
                      
                      <div className="bg-white/5 rounded-xl p-4 border border-white/5">
                        <p className="text-xs text-amber-400 font-semibold mb-1 uppercase">Success Story</p>
                        <p className="text-sm text-white/90 italic">"{service.caseStudy}"</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Process Section */}
          <section className="container mx-auto px-6 mb-32">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">How We Work</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Our proven methodology ensures transparency, speed, and exceptional quality at every stage of your project.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { step: "01", title: "Discovery", desc: "We dive deep into your business goals and technical requirements." },
                { step: "02", title: "Strategy", desc: "We design a comprehensive roadmap and technical architecture." },
                { step: "03", title: "Development", desc: "Agile execution with regular updates and rapid iterations." },
                { step: "04", title: "Launch", desc: "Seamless deployment, training, and ongoing support." }
              ].map((item, i) => (
                <div key={i} className="relative p-6 rounded-2xl border border-zinc-800 bg-zinc-900 text-center group hover:border-amber-500/50 transition-colors">
                  <div className="text-6xl font-bold text-zinc-800 absolute top-4 right-4 group-hover:text-amber-500/10 transition-colors select-none">
                    {item.step}
                  </div>
                  <div className="relative z-10">
                    <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 font-bold mb-6 mx-auto">
                      {item.step}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="container mx-auto px-6">
            <div className="relative rounded-3xl overflow-hidden bg-zinc-900 border border-zinc-800 p-12 md:p-24 text-center">
              <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-20"></div>
              <div className="relative z-10 max-w-3xl mx-auto">
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-8">
                  Ready to Transform Your Business?
                </h2>
                <p className="text-xl text-muted-foreground mb-10">
                  Join 500+ forward-thinking companies that have partnered with FortifyTek to build the future.
                </p>
                <Button 
                  size="lg" 
                  className="bg-white text-black hover:bg-zinc-200 px-10 h-14 text-lg font-bold"
                  onClick={() => navigate('/contact')}
                >
                  Schedule a Consultation
                </Button>
              </div>
            </div>
          </section>
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default Services;
